import { isBoutiqueSide } from "@/features/invoices/invoice-utils";
import { useCallback, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
  Text,
} from "@chakra-ui/react";
import { LightPayment } from "@/features/invoices/payment/payment.type";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AddBankRemittanceReceiptModal from "@/shared/add-bank-remittance-receipt-modal";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import AppLabel from "@/features/ui/app-label";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";

interface InvoicePaymentsPanelProps {
  payments: LightPayment[];
  // 송금 영수증 업로드 모달을 닫은 뒤(송금 완료 처리 후) — 송금 상태를 다시 불러올 때 쓴다
  onReceiptModalClose?: () => void;
}

// 인보이스(프로포마·OC)의 관련 송금 요청 — 송금 상태를 보여 주고 영수증을 올려 송금 완료 처리한다.
// 바이어 송금 전에 부티크 해외 송금 영수증을 먼저 올리면 선송금 안내를 띄운다.
export default function InvoicePaymentsPanel({
  payments,
  onReceiptModalClose,
}: InvoicePaymentsPanelProps) {
  const { tTitle, tCurrencyString, tNumberString } = useI18nHelper();
  const [selectedPayment, setSelectedPayment] = useState<LightPayment | undefined>(undefined);
  const [pendingBoutiquePayment, setPendingBoutiquePayment] = useState<LightPayment | undefined>(undefined);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleUploadReceipt = useCallback((payment: LightPayment) => {
    const isBoutiquePayment = payment.sender.type === "AGENCY" && isBoutiqueSide(payment.receiver.type);
    const buyerRemitted = payments.some(p => p.sender.type === "BUYER" && p.receiver.type === "AGENCY" && p.isRemitted);
    if (isBoutiquePayment && !buyerRemitted) {
      setPendingBoutiquePayment(payment);
    } else {
      setSelectedPayment(payment);
    }
  }, [payments]);

  // 업로드된 송금 영수증 파일 다운로드(서버 presigned URL)
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const handleDownloadReceipt = useCallback(
    (fileId: number) => {
      fireDownloadUnoFile({ unoFileId: fileId }, {});
    },
    [fireDownloadUnoFile]
  );

  if (payments.length === 0) {
    return null;
  }

  return (
    <>
      <AppPanel title={tTitle("common:related_payments")}>
        <Flex flexDirection={"column"} gap={"8px"}>
          {payments.map((payment) => (
            <Flex
              key={payment.id}
              flexDirection={"column"}
              gap={"8px"}
              padding={"12px"}
              border={"1px solid #E0E0DC"}
              borderRadius={"6px"}
              backgroundColor={"white"}
            >
              <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
                <Flex alignItems={"center"} gap={"8px"}>
                  <Text fontSize={"14px"} fontWeight={"600"}>
                    {payment.type === "ADJUSTMENT"
                      ? tTitle("common:payment_type.adjustment")
                      : payment.type === "UNO_FEE"
                        ? tTitle("common:payment_type.uno_fee")
                        : tTitle("common:payment_type.product")}
                  </Text>
                  <AppLabel
                    nameKey={isBoutiqueSide(payment.receiver.type)
                      ? "common:overseas_remittance"
                      : "common:domestic_remittance"}
                    level={"LIGHT"}
                    size={"small"}
                    isToUpper={false}
                    minW={"0"}
                  />
                  <AppRemittanceStatusLabel
                    to={payment.type === "ADJUSTMENT" && payment.receiver.type === "BUYER" ? "REFUND" : undefined}
                    value={payment.isRemitted ? "REMITTED" : "UNREMITTED"}
                  />
                </Flex>
                <AppButton
                  isDisabled={payment.isRemitted}
                  onClick={() => handleUploadReceipt(payment)}
                >
                  {tTitle("bank_remittance_receipts:upload_bank_remittance_receipt")}
                </AppButton>
              </Flex>
              <Flex gap={"8px"} alignItems={"center"}>
                <Text fontSize={"12px"} color={"#6F6F6C"}>From:</Text>
                <Text fontSize={"12px"} fontWeight={"600"}>{payment.sender.name}</Text>
                <Text fontSize={"12px"} color={"#6F6F6C"}>To:</Text>
                <Text fontSize={"12px"} fontWeight={"600"}>
                  {isBoutiqueSide(payment.receiver.type) && payment.receiver.orgName
                    ? `[${payment.receiver.name}] ${payment.receiver.orgName}`
                    : payment.receiver.name}
                </Text>
              </Flex>
              <Flex gap={"4px"} alignItems={"center"}>
                <Text fontSize={"12px"} color={"#6F6F6C"}>금액:</Text>
                <Text fontSize={"12px"} fontWeight={"600"}>{tCurrencyString(payment.amount)}</Text>
              </Flex>
              {payment.bankRemittanceReceipts
                ?.filter((receipt) => receipt.file)
                .map((receipt) => (
                  <Flex key={receipt.id} gap={"4px"} alignItems={"center"}>
                    <Text fontSize={"12px"} color={"#6F6F6C"}>송금 영수증:</Text>
                    <Text
                      fontSize={"12px"}
                      fontWeight={"600"}
                      color={"#1272EF"}
                      cursor={"pointer"}
                      textDecoration={"underline"}
                      onClick={() => handleDownloadReceipt(receipt.file!.id)}
                    >
                      {receipt.file!.name}
                    </Text>
                  </Flex>
                ))}
              {(() => {
                if (!isBoutiqueSide(payment.receiver.type)) return null;
                const receipt = payment.bankRemittanceReceipts?.[0];
                if (!receipt) return null;
                const exchangeRate = receipt.exchangeRate;
                const bankFee = receipt.bankFee;
                const krwAmount = exchangeRate != null && payment.amount?.value != null
                  ? Math.floor(payment.amount.value * exchangeRate + (bankFee?.value ?? 0))
                  : null;
                return (
                  <>
                    {exchangeRate != null && (
                      <Flex gap={"4px"} alignItems={"center"}>
                        <Text fontSize={"12px"} color={"#6F6F6C"}>환율:</Text>
                        <Text fontSize={"12px"} fontWeight={"600"}>{tNumberString(exchangeRate)}</Text>
                      </Flex>
                    )}
                    {bankFee != null && (
                      <Flex gap={"4px"} alignItems={"center"}>
                        <Text fontSize={"12px"} color={"#6F6F6C"}>은행 수수료:</Text>
                        <Text fontSize={"12px"} fontWeight={"600"}>{tCurrencyString(bankFee)}</Text>
                      </Flex>
                    )}
                    {krwAmount != null && (
                      <Flex gap={"4px"} alignItems={"center"}>
                        <Text fontSize={"12px"} color={"#6F6F6C"}>송금 원화 금액:</Text>
                        <Text fontSize={"12px"} fontWeight={"600"}>{tCurrencyString({ value: krwAmount, currency: "KRW" })}</Text>
                      </Flex>
                    )}
                  </>
                );
              })()}
            </Flex>
          ))}
        </Flex>
      </AppPanel>
      {selectedPayment && (
        <AddBankRemittanceReceiptModal
          isOpen={!!selectedPayment}
          onClose={() => {
            setSelectedPayment(undefined);
            onReceiptModalClose?.();
          }}
          payment={selectedPayment as any}
        />
      )}
      <AlertDialog
        isOpen={!!pendingBoutiquePayment}
        leastDestructiveRef={cancelRef}
        onClose={() => setPendingBoutiquePayment(undefined)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              선송금 처리 안내
            </AlertDialogHeader>
            <AlertDialogBody>
              바이어의 송금 영수증이 아직 업로드되지 않았습니다. 지금 부티크 해외 송금 영수증을 먼저 업로드하면 선송금 처리됩니다. 계속하시겠습니까?
            </AlertDialogBody>
            <AlertDialogFooter gap={"8px"}>
              <Button ref={cancelRef} onClick={() => setPendingBoutiquePayment(undefined)}>
                취소
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  setSelectedPayment(pendingBoutiquePayment);
                  setPendingBoutiquePayment(undefined);
                }}
              >
                확인
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
